import {
  Badge,
  Button,
  Divider,
  IconButton,
  Link,
  makeStyles,
  Menu,
  MenuItem,
  Tooltip,
} from "@material-ui/core";
import { SvgIconComponent } from "@material-ui/icons";
import React, { useState } from "react";

import {
  useSearchBatched,
  SearchCoreMethods,
} from "@ofqual/portal-core/core/hooks";
import { QueryParams } from "@ofqual/portal-core/core/types";
const useStyles = makeStyles((theme) => ({
  button: {
    color: theme.palette.secondary.contrastText,
  },
  badge: {
    padding: 3,
    textTransform: "none",

    "& .MuiBadge-badge": {
      height: 17,
      minWidth: 17,
      top: 5,

      "& .MuiSvgIcon-root": {
        fontSize: 5,
      },
    },
  },
  filterMenu: {
    width: "400px",
    padding: "15px",
  },
  divider: {
    marginBottom: "10px",
    marginTop: "10px",
  },
}));

export type FilterMenuProps = {
  title?: string;
  Icon: SvgIconComponent;
  dataTest?: string;
  whitelist?: string[];
  children: (
    workingParams: QueryParams,
    dirtyParams: string[],
    addParam: (
      name: string,
      value: string | number | string[] | number[] | undefined
    ) => void,
    updateMultipleParams: (
      params: { [key: string]: string | number | string[] | number[] | undefined }
    ) => void
  ) => React.ReactNode;
  search: SearchCoreMethods;
  classNameButton?: string;
  showMenuAboveButton?: boolean;
};

export const FilterMenu: React.FC<FilterMenuProps> = ({
  title,
  Icon,
  children,
  dataTest,
  whitelist,
  search,
  classNameButton,
  showMenuAboveButton,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const classes = useStyles();

  const handleMenuOpen = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const {
    addParam,
    batchApplyParams,
    workingParams,
    dirtyParams,
    searchParamsFiltersOnly,
    batchClearParams,
    updateMultipleParams,
  } = useSearchBatched(search, whitelist);

  const selectedParams = whitelist
    ? Object.keys(searchParamsFiltersOnly).filter((param) =>
        whitelist.includes(param)
      )
    : Object.keys(searchParamsFiltersOnly);

  const currentParamKeys = Object.keys(workingParams);
  const currentParams = currentParamKeys.filter(
    (x) => !dirtyParams.includes(x)
  );
  return (
    <>
      {title ? (
        <Button
          startIcon={<Icon />}
          aria-haspopup="true"
          onClick={handleMenuOpen}
          className={classNameButton || classes.button}
          data-test={dataTest}
        >
          <Badge badgeContent={selectedParams.length} className={classes.badge}>
            {title}
          </Badge>
        </Button>
      ) : (
        <Tooltip title="Filter" aria-label="Filter">
          <IconButton
            onClick={handleMenuOpen}
            className={classNameButton || classes.button}
            data-test={dataTest}
          >
            <Badge
              badgeContent={selectedParams.length || currentParams.length}
              className={classes.badge}
            >
              <Icon />
            </Badge>
          </IconButton>
        </Tooltip>
      )}

      <Menu
        PaperProps={{ className: classes.filterMenu }}
        getContentAnchorEl={null}
        anchorOrigin={{
          vertical: showMenuAboveButton ? "top" : "bottom",
          horizontal: showMenuAboveButton ? "left" : "center",
        }}
        transformOrigin={{
          vertical: showMenuAboveButton ? "bottom" : "top",
          horizontal: showMenuAboveButton ? "left" : "center",
        }}
        anchorEl={anchorEl}
        keepMounted
        open={!!anchorEl}
        onClose={handleMenuClose}
        data-test={`menu-${dataTest}`}
      >
        <MenuItem disabled>{title || "Filters"}</MenuItem>
        <Divider className={classes.divider} />
        {children(workingParams, dirtyParams, addParam, updateMultipleParams)}
        <Divider className={classes.divider} />
        <Link
          className="linkButton"
          data-test="apply-button"
          onClick={() => {
            batchApplyParams();
            setAnchorEl(null);
          }}
        >
          {dirtyParams.length ? <strong>APPLY</strong> : "APPLY"}
        </Link>
        {" | "}
        <Link
          className="linkButton"
          data-test="clear-button"
          onClick={() => {
            batchClearParams();
            setAnchorEl(null);
          }}
        >
          CLEAR
        </Link>
      </Menu>
    </>
  );
};
